import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthProvider';
import './VirtualKeyboard.scss';

// On-screen AZERTY keyboard for touch "all-in-one" stations without a physical keyboard.
// Shown whenever an editable text field gets focus (and the setting is on). Keys are pressed
// on pointerdown with preventDefault, so the focused field never loses focus to the keyboard.

const TEXT_INPUT_TYPES = ['text', 'search', 'email', 'tel', 'url', 'password', 'number'];

const LETTERS = [
    ['a', 'z', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p', 'BACKSPACE'],
    ['q', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', 'm'],
    ['SHIFT', 'w', 'x', 'c', 'v', 'b', 'n', "'", ',', '.'],
    ['SYMBOLS', '@', 'SPACE', '-', 'ENTER', 'HIDE'],
];

const SYMBOLS = [
    ['é', 'è', 'ê', 'ë', 'à', 'â', 'ù', 'û', 'ç', 'ô', 'BACKSPACE'],
    ['î', 'ï', '#', '&', '*', '(', ')', '_', '/', ':'],
    [';', '"', '!', '?', '+', '=', '%', '€', '$', '|'],
    ['LETTERS', '@', 'SPACE', '-', 'ENTER', 'HIDE'],
];

// Calculator-style numeric pad shown to the right of the letters, in both layouts.
const NUMPAD = [
    ['7', '8', '9'],
    ['4', '5', '6'],
    ['1', '2', '3'],
    ['0', '.'],
];

const isEditable = (el) => {
    if (!el || el.disabled || el.readOnly) return false;
    if (el.tagName === 'TEXTAREA') return true;
    if (el.tagName !== 'INPUT') return false;
    return TEXT_INPUT_TYPES.includes((el.getAttribute('type') || 'text').toLowerCase());
};

// React tracks controlled input values itself — assigning el.value directly is ignored, so the
// native setter is called and an `input` event dispatched for onChange to fire.
const setNativeValue = (el, value) => {
    const proto = el.tagName === 'TEXTAREA' ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
    Object.getOwnPropertyDescriptor(proto, 'value').set.call(el, value);
    el.dispatchEvent(new Event('input', { bubbles: true }));
};

// number/email inputs don't expose a caret position (selectionStart throws or is null) —
// for those, edits always happen at the end of the value.
const getSelection = (el) => {
    try {
        if (typeof el.selectionStart === 'number') return [el.selectionStart, el.selectionEnd];
    } catch (e) { /* unsupported input type */ }
    return [el.value.length, el.value.length];
};

const setCaret = (el, pos) => {
    try { el.setSelectionRange(pos, pos); } catch (e) { /* unsupported input type */ }
};

const insertText = (el, text) => {
    const [start, end] = getSelection(el);
    const value = el.value.slice(0, start) + text + el.value.slice(end);
    if (el.maxLength > 0 && value.length > el.maxLength) return;
    setNativeValue(el, value);
    setCaret(el, start + text.length);
};

const deleteBackward = (el) => {
    const [start, end] = getSelection(el);
    if (start === end && start === 0) return;
    const from = start === end ? start - 1 : start;
    setNativeValue(el, el.value.slice(0, from) + el.value.slice(end));
    setCaret(el, from);
};

const pressEnter = (el) => {
    if (el.tagName === 'TEXTAREA') {
        insertText(el, '\n');
        return;
    }
    const init = { key: 'Enter', code: 'Enter', bubbles: true, cancelable: true };
    const notCanceled = el.dispatchEvent(new KeyboardEvent('keydown', init));
    el.dispatchEvent(new KeyboardEvent('keyup', init));
    // a dispatched keydown doesn't trigger the browser's implicit form submission
    if (notCanceled && el.form) el.form.requestSubmit();
};

const KEY_LABELS = {
    BACKSPACE: <i className="ti ti-backspace" />,
    SHIFT: <i className="ti ti-arrow-big-up" />,
    SPACE: '',
    ENTER: <i className="ti ti-corner-down-left" />,
    HIDE: <i className="ti ti-keyboard-hide" />,
    SYMBOLS: '?123',
    LETTERS: 'ABC',
};

export default function VirtualKeyboard() {
    const { settings } = useAuth();
    const enabledVal = settings?.['general_general_settings_virtual_keyboard_enabled'];
    const enabled = enabledVal === true || enabledVal === 'true';

    const [target, setTarget] = useState(null);
    const [layout, setLayout] = useState('letters');
    const [shift, setShift] = useState('off'); // off | once | lock
    const panelRef = useRef(null);
    const repeatRef = useRef(null);

    // Track the focused field. inputmode="none" keeps the OS touch keyboard (Windows/Android)
    // from popping up on top of ours; the original value is restored on blur.
    useEffect(() => {
        if (!enabled) return;
        const onFocusIn = (e) => {
            const el = e.target;
            if (!isEditable(el)) return;
            if (!el.hasAttribute('data-vk-inputmode')) {
                el.setAttribute('data-vk-inputmode', el.getAttribute('inputmode') ?? '');
                el.setAttribute('inputmode', 'none');
            }
            setTarget(el);
        };
        const onFocusOut = (e) => {
            const el = e.target;
            if (el.hasAttribute?.('data-vk-inputmode')) {
                const prev = el.getAttribute('data-vk-inputmode');
                if (prev) el.setAttribute('inputmode', prev); else el.removeAttribute('inputmode');
                el.removeAttribute('data-vk-inputmode');
            }
            // focus may be moving straight to another field — let focusin run first
            setTimeout(() => {
                if (!isEditable(document.activeElement)) setTarget(null);
            }, 0);
        };
        document.addEventListener('focusin', onFocusIn);
        document.addEventListener('focusout', onFocusOut);
        if (isEditable(document.activeElement)) onFocusIn({ target: document.activeElement });
        return () => {
            document.removeEventListener('focusin', onFocusIn);
            document.removeEventListener('focusout', onFocusOut);
            setTarget(null);
        };
    }, [enabled]);

    // Expose the keyboard height so the layout, offcanvases and modals shrink above it
    // instead of having their bottom (e.g. the cart's payment fields) hidden behind it.
    // A `resize` event is fired whenever that height changes, so already-open portalled
    // dropdowns (e.g. ImprovedSelect) recompute their position/max-height above the keyboard.
    useLayoutEffect(() => {
        const root = document.documentElement;
        if (!target || !panelRef.current) {
            if (root.style.getPropertyValue('--vk-height')) {
                root.style.removeProperty('--vk-height');
                window.dispatchEvent(new Event('resize'));
            }
            document.body.classList.remove('vk-open');
            return;
        }
        const apply = () => {
            if (!panelRef.current) return;
            const height = `${panelRef.current.offsetHeight}px`;
            if (root.style.getPropertyValue('--vk-height') === height) return;
            root.style.setProperty('--vk-height', height);
            window.dispatchEvent(new Event('resize'));
        };
        apply();
        document.body.classList.add('vk-open');
        const ro = new ResizeObserver(apply);
        ro.observe(panelRef.current);
        const t = setTimeout(() => target.scrollIntoView?.({ block: 'nearest' }), 50);
        return () => { ro.disconnect(); clearTimeout(t); };
    }, [target]);

    useEffect(() => () => {
        document.documentElement.style.removeProperty('--vk-height');
        document.body.classList.remove('vk-open');
    }, []);

    const stopRepeat = useCallback(() => {
        clearTimeout(repeatRef.current);
        clearInterval(repeatRef.current);
        repeatRef.current = null;
    }, []);

    useEffect(() => stopRepeat, [stopRepeat]);

    const handleKey = (key) => {
        const el = target;
        if (!el || !document.contains(el)) { setTarget(null); return; }
        switch (key) {
            case 'BACKSPACE': deleteBackward(el); break;
            case 'SPACE': insertText(el, ' '); break;
            case 'ENTER': pressEnter(el); break;
            case 'HIDE': el.blur(); break;
            case 'SYMBOLS': setLayout('symbols'); break;
            case 'LETTERS': setLayout('letters'); break;
            case 'SHIFT': setShift(s => (s === 'off' ? 'once' : s === 'once' ? 'lock' : 'off')); break;
            default: {
                const upper = layout === 'letters' && shift !== 'off';
                insertText(el, upper ? key.toUpperCase() : key);
                if (shift === 'once') setShift('off');
            }
        }
    };

    const onKeyPointerDown = (e, key) => {
        e.preventDefault();
        handleKey(key);
        if (key === 'BACKSPACE') {
            stopRepeat();
            repeatRef.current = setTimeout(() => {
                repeatRef.current = setInterval(() => handleKey('BACKSPACE'), 60);
            }, 450);
        }
    };

    if (!enabled || !target) return null;

    const rows = layout === 'letters' ? LETTERS : SYMBOLS;

    const renderKey = (key) => {
        const special = key.length > 1 && KEY_LABELS[key] !== undefined;
        const label = special
            ? KEY_LABELS[key]
            : (layout === 'letters' && shift !== 'off' ? key.toUpperCase() : key);
        const classes = ['virtual-keyboard__key'];
        if (special) classes.push(`virtual-keyboard__key--${key.toLowerCase()}`);
        if (key === 'SHIFT' && shift !== 'off') classes.push('virtual-keyboard__key--active');
        if (key === 'SHIFT' && shift === 'lock') classes.push('virtual-keyboard__key--locked');
        return (
            <button
                key={key}
                type="button"
                tabIndex={-1}
                className={classes.join(' ')}
                onPointerDown={e => onKeyPointerDown(e, key)}
            >
                {label}
            </button>
        );
    };

    return (
        <div
            ref={panelRef}
            className="virtual-keyboard"
            onMouseDown={e => e.preventDefault()}
            onPointerUp={stopRepeat}
            onPointerLeave={stopRepeat}
            onPointerCancel={stopRepeat}
        >
            <div className="virtual-keyboard__inner">
                <div className="virtual-keyboard__block virtual-keyboard__block--main">
                    {rows.map((row, i) => (
                        <div key={i} className="virtual-keyboard__row">{row.map(renderKey)}</div>
                    ))}
                </div>
                <div className="virtual-keyboard__block virtual-keyboard__block--numpad">
                    {NUMPAD.map((row, i) => (
                        <div key={i} className="virtual-keyboard__row">
                            {row.map(key => (
                                <button
                                    key={key}
                                    type="button"
                                    tabIndex={-1}
                                    className={`virtual-keyboard__key virtual-keyboard__key--num${key === '0' ? ' virtual-keyboard__key--zero' : ''}`}
                                    onPointerDown={e => onKeyPointerDown(e, key)}
                                >
                                    {key}
                                </button>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
